import { useMemo } from 'react';
import type { KaliaCalendarConfig, KaliaEvent, KaliaResource, PositionedEvent } from './types';

function parseTime(timeStr: string): number {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
}

export function timeToPixel(minutes: number, dayStartMinutes: number, slotDuration: number, slotHeight: number): number {
    return ((minutes - dayStartMinutes) / slotDuration) * slotHeight;
}

function getEventMinutes(date: Date, timezone: string): number {
    const formatted = date.toLocaleString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const [h, m] = formatted.split(':').map(Number);
    return h * 60 + m;
}

function isBlockerEvent(event: KaliaEvent): boolean {
    return event.display === 'background' && event.metadata?.type === 'BLOCKED';
}

function computeOverlapLanes(events: (KaliaEvent & { top: number; height: number })[]): PositionedEvent[] {
    if (events.length === 0) return [];

    const conflicts = (
        a: KaliaEvent & { top: number; height: number },
        b: KaliaEvent & { top: number; height: number }
    ): boolean => {
        return a.top < b.top + b.height && a.top + a.height > b.top;
    };

    const sorted = [...events].sort((a, b) => a.top - b.top || b.height - a.height);
    const lanes: (KaliaEvent & { top: number; height: number })[][] = [];
    const assigned: (KaliaEvent & { top: number; height: number; lane: number })[] = [];

    for (const ev of sorted) {
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
            if (!lanes[i].some((other) => conflicts(ev, other))) {
                lanes[i].push(ev);
                assigned.push({ ...ev, lane: i });
                placed = true;
                break;
            }
        }
        if (!placed) {
            assigned.push({ ...ev, lane: lanes.length });
            lanes.push([ev]);
        }
    }

    const groups: (typeof assigned)[] = [];
    const used = new Set<number>();

    for (let i = 0; i < assigned.length; i++) {
        if (used.has(i)) continue;
        const group = [assigned[i]];
        used.add(i);
        const iEnd = assigned[i].top + assigned[i].height;

        for (let j = i + 1; j < assigned.length; j++) {
            if (used.has(j)) continue;
            const jEnd = assigned[j].top + assigned[j].height;
            const overlapsAny = group.some((g) => {
                const gEnd = g.top + g.height;
                return assigned[j].top < gEnd && jEnd > g.top;
            });
            if (overlapsAny) {
                group.push(assigned[j]);
                used.add(j);
            }
        }
        groups.push(group);
    }

    const result: PositionedEvent[] = [];
    for (const group of groups) {
        const maxLane = Math.max(...group.map((e) => e.lane)) + 1;
        for (const ev of group) {
            result.push({ ...ev, totalLanes: maxLane });
        }
    }

    return result;
}

export function useKaliaCalendarLayout(
    resources: KaliaResource[],
    events: KaliaEvent[],
    config: KaliaCalendarConfig
) {
    const dayStartMinutes = useMemo(() => parseTime(config.slotMinTime), [config.slotMinTime]);
    const dayEndMinutes = useMemo(() => parseTime(config.slotMaxTime), [config.slotMaxTime]);
    const totalSlots = useMemo(
        () => (dayEndMinutes - dayStartMinutes) / config.slotDuration,
        [dayStartMinutes, dayEndMinutes, config.slotDuration]
    );
    const totalHeight = useMemo(
        () => totalSlots * config.slotHeight,
        [totalSlots, config.slotHeight]
    );

    const timeSlots = useMemo(() => {
        const slots: string[] = [];
        for (let m = dayStartMinutes; m < dayEndMinutes; m += config.slotDuration) {
            const h = Math.floor(m / 60);
            const min = m % 60;
            slots.push(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
        }
        return slots;
    }, [dayStartMinutes, dayEndMinutes, config.slotDuration]);

    const eventsByResource = useMemo(() => {
        const map = new Map<string, { foreground: PositionedEvent[]; background: KaliaEvent[] }>();

        for (const resource of resources) {
            map.set(resource.id, { foreground: [], background: [] });
        }

        const foregroundByResource = new Map<string, (KaliaEvent & { top: number; height: number })[]>();

        for (const event of events) {
            if (!event.start || !event.end) continue;

            const startMin = getEventMinutes(event.start, config.timezone);
            const endMin = getEventMinutes(event.end, config.timezone);

            const clampedStart = Math.max(startMin, dayStartMinutes);
            const clampedEnd = Math.min(endMin, dayEndMinutes);
            if (clampedStart >= clampedEnd) continue;

            const top = timeToPixel(clampedStart, dayStartMinutes, config.slotDuration, config.slotHeight);
            const height = Math.max(
                timeToPixel(clampedEnd, dayStartMinutes, config.slotDuration, config.slotHeight) - top,
                10
            );

            const isBlocker = isBlockerEvent(event);
            const isForeground = !event.display || event.display !== 'background' || isBlocker;

            for (const resourceId of event.resourceIds) {
                if (!map.has(resourceId)) continue;

                if (isForeground) {
                    if (!foregroundByResource.has(resourceId)) {
                        foregroundByResource.set(resourceId, []);
                    }
                    foregroundByResource.get(resourceId)!.push({ ...event, top, height });
                } else {
                    map.get(resourceId)!.background.push(event);
                }
            }
        }

        for (const resource of resources) {
            const rid = resource.id;
            const entry = map.get(rid)!;

            entry.foreground = computeOverlapLanes(foregroundByResource.get(rid) || []);
        }

        return map;
    }, [resources, events, config, dayStartMinutes, dayEndMinutes]);

    return {
        dayStartMinutes,
        dayEndMinutes,
        totalSlots,
        totalHeight,
        timeSlots,
        eventsByResource,
    };
}
